// The status of a task in the Excel report (Documentation/excel-report), shown as its cell's colour (colours.js):
// one rule for every task cell (A-L, V00-V14, Reserved area, Stacked out, Borders sprayed, Plot size, Pegs up). First
// match wins:
// 1. Done (a done date) - 2. Cancelled - 3. Not needed (Planned or Time interval says so)
// 4. Overdue: not done, and the end of the Planned value - or, without one, of the Time interval - lies before the
//    export date. An end that cannot be told ("7 DAA", "BBCH 32", "after harvest") is never overdue.
// 5. Planned (a Planned value) - 6. Missing info (Time interval "?", "Maybe", "TBD" or empty) - 7. Time interval
// The Planned value counts whenever there is one, also when it lies after the Time interval (agreed with Agrolab).
// The end is periodEnd's (../periodEnd.js), the same as on the screen (taskStatus.js).
import { cleanText as text, periodEnd } from '../periodEnd';

export { periodEnd };

export const STATUS = {
  done: 'done', cancelled: 'cancelled', notNeeded: 'not-needed', overdue: 'overdue', planned: 'planned', missing: 'missing', interval: 'interval',
};

export const STATUS_LABELS = {
  [STATUS.done]: 'Done',
  [STATUS.cancelled]: 'Cancelled',
  [STATUS.notNeeded]: 'Not needed',
  [STATUS.overdue]: 'Overdue',
  [STATUS.planned]: 'Planned',
  [STATUS.missing]: 'Missing info',
  [STATUS.interval]: 'Time interval',
};

/** The statuses whose task is no longer to do (the next task is the first one that is not). */
export const CLOSED_STATUSES = [STATUS.done, STATUS.cancelled, STATUS.notNeeded];

const MISSING = ['', '?', 'maybe', 'tbd'];

/** Whether a Time interval means "missing info" ("?", "Maybe", "TBD" or empty). */
export const isMissingValue = (value) => MISSING.includes(text(value).toLowerCase());
/** Whether a Time interval or Planned value says "Not needed". */
export const isNotNeededValue = (value) => text(value).toLowerCase() === 'not needed';

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * A task's status in the report: { status, decidedBy, end } - decidedBy is the field that gives it ('done', 'planned'
 * or 'timeinterval'), end the last day of the Planned / Time interval value when
 * it could be told. Without a task: { status: null }.
 * @param {{ donedate?, cancelled?, planned_interval?, planned_date?, timeinterval?, timeinterval_text? } | null} task
 * @param {Date} exportDate - the day of the export (overdue means: ended before it)
 */
export function cellStatus(task, exportDate) {
  if (!task) return { status: null, decidedBy: null, end: null };
  if (task.donedate) return { status: STATUS.done, decidedBy: 'done', end: null };
  if (task.cancelled) return { status: STATUS.cancelled, decidedBy: 'done', end: null };

  const planned = text(task.planned_interval) || text(task.planned_date);
  // The Time interval's end comes from timeinterval only (a date, or an old text); its text never gives one
  const interval = text(task.timeinterval);
  const words = text(task.timeinterval_text);
  if (isNotNeededValue(planned)) return { status: STATUS.notNeeded, decidedBy: 'planned', end: null };
  if (!planned && (isNotNeededValue(interval) || (!interval && isNotNeededValue(words)))) return { status: STATUS.notNeeded, decidedBy: 'timeinterval', end: null };

  const today = startOfDay(exportDate);
  if (planned) {
    const end = periodEnd(planned);
    return { status: end && end < today ? STATUS.overdue : STATUS.planned, decidedBy: 'planned', end };
  }
  if (isMissingValue(interval) && isMissingValue(words)) return { status: STATUS.missing, decidedBy: 'timeinterval', end: null };
  const end = periodEnd(interval);
  return { status: end && end < today ? STATUS.overdue : STATUS.interval, decidedBy: 'timeinterval', end };
}
